import * as THREE from 'three';
import { allMeshes, loadModel, loadPaletteTexture, preloadModels } from './gltf-cache';

export interface ModelInstance {
  model: string;
  x: number;
  y: number;
  z: number;
  rotationY: number;
}

/**
 * Side of the square area one InstancedMesh covers (2026-10-15, QA #9: "GPU at
 * 94%, heavy FPS drops"). One mesh per model *for the whole floor* meant every
 * mesh's bounds spanned the dungeon, so frustum culling could never reject
 * anything: all ~300k layout triangles were drawn every frame — twice, since
 * the SSAO pass re-renders the scene — even with the camera facing a wall.
 * Four grid units (16m) is about one room, so a room behind the camera costs
 * nothing, while the draw-call count stays in the low hundreds.
 */
export const LAYOUT_CHUNK_M = 16;

/** Placements bucketed into `chunkM`-square cells, in first-seen order. Pure; exported for tests. */
export function chunkPlacements<T extends { x: number; z: number }>(placements: readonly T[], chunkM: number): T[][] {
  const cells = new Map<string, T[]>();
  for (const p of placements) {
    const key = `${Math.floor(p.x / chunkM)}:${Math.floor(p.z / chunkM)}`;
    const cell = cells.get(key);
    if (cell) cell.push(p); else cells.set(key, [p]);
  }
  return [...cells.values()];
}

/**
 * design/gdd/procedural-layout-assembly.md Acceptance Criteria #9 —
 * InstancedMesh per unique model (not one Object3D per placement), so a
 * ~30-room layout with a handful of repeated prefabs costs a handful of
 * draw calls, not hundreds.
 *
 * `paletteFileName` — game-concept.md §10 Risk #3's per-stratum recolor
 * (see config/stratum-palette.ts). `gltf-cache.ts` caches one template
 * scene (and its material) per model name, shared by every layout built
 * for the whole app session — mutating `mesh.material` directly would
 * leak one stratum's palette into another's layout the next time this
 * model appears. Each source material is cloned once (never twice) before
 * its `.map` is swapped, same "clone once per instance, never mutate the
 * shared cache" rule character-visual.ts's applySkin() already follows.
 */
export async function buildInstancedLayout(instances: ModelInstance[], paletteFileName?: string): Promise<THREE.Group> {
  const group = new THREE.Group();
  const byModel = new Map<string, ModelInstance[]>();
  for (const inst of instances) {
    const list = byModel.get(inst.model);
    if (list) list.push(inst); else byModel.set(inst.model, [inst]);
  }

  const [, paletteTexture] = await Promise.all([
    preloadModels([...byModel.keys()]),
    paletteFileName ? loadPaletteTexture(paletteFileName) : Promise.resolve(null),
  ]);

  const placementMatrix = new THREE.Matrix4();
  const finalMatrix = new THREE.Matrix4();
  for (const [modelName, placements] of byModel) {
    const scene = await loadModel(modelName); // cached — resolves without a new network request
    // Chunked by area (2026-10-15, QA #9) — see `chunkPlacements`.
    const chunks = chunkPlacements(placements, LAYOUT_CHUNK_M);
    // One InstancedMesh per sub-mesh the model has (usually 1, sometimes more —
    // e.g. chest_gold's base + lid), each sharing this model's placements.
    for (const { mesh, localMatrix } of allMeshes(scene)) {
      let material = mesh.material as THREE.Material;
      if (paletteTexture) {
        // Once per model, shared by every chunk — a clone per chunk would be a
        // material (and a texture upload) per chunk for an identical result.
        const clone = material.clone();
        (clone as THREE.MeshStandardMaterial).map = paletteTexture;
        clone.needsUpdate = true;
        material = clone;
      }
      for (const chunk of chunks) {
        const instancedMesh = new THREE.InstancedMesh(mesh.geometry, material, chunk.length);
        instancedMesh.castShadow = true;
        instancedMesh.receiveShadow = true;

        chunk.forEach((p, i) => {
          placementMatrix.makeRotationY(p.rotationY);
          placementMatrix.setPosition(p.x, p.y, p.z);
          finalMatrix.multiplyMatrices(placementMatrix, localMatrix);
          instancedMesh.setMatrixAt(i, finalMatrix);
        });
        instancedMesh.instanceMatrix.needsUpdate = true;
        // Bounds of *these* instances, so the renderer's frustum test and the
        // camera's collision raycasts can reject the chunk as a whole.
        instancedMesh.computeBoundingSphere();
        group.add(instancedMesh);
      }
    }
  }

  return group;
}
